import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, retryOperation } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { Loader2, CheckCircle, Mail, Phone, Home, Sun, Sprout, Check, X } from 'lucide-react';
import Logo from '../components/Logo';
import PackageCard from '../components/shop/PackageCard';

type GrowingSpace = 'raised_bed' | 'in_ground' | 'containers' | 'none_yet';
type SunlightHours = 'full_sun' | 'full_to_part_sun' | 'part_sun_part_shade' | 'full_shade';
type FallGardenVibe = 'fall_greens' | 'cool_weather_veggie';
type SunExposure = 'full_sun' | 'part_sun' | 'shade';
type GardenSetup = 'in_ground_raised' | 'containers_bags' | 'no_setup';

interface OnboardingFormData {
  fallGardenVibe: FallGardenVibe | null;
  firstName: string;
  zipCode: string;
  sunExposure: SunExposure | null;
  gardenSetup: GardenSetup | null;
  email: string;
  phone: string;
}

export default function LandingPage1() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(() => {
    const stepParam = parseInt(searchParams.get('step') || '1', 10);
    // Ensure step is within valid range (1 to 5)
    return Math.max(1, Math.min(stepParam, 5));
  });
  const [formData, setFormData] = useState<OnboardingFormData>({
    fallGardenVibe: null,
    firstName: '',
    zipCode: '',
    sunExposure: null,
    gardenSetup: null,
    email: user?.email || '',
    phone: '',
  });

  type SuggestedProduct = 'Digital Garden Plan' | 'The Easy Grow Box' | 'The Guided Garden Box';

  // Product mapping for fetching from database
  const PRODUCT_INFO = {
    'Digital Garden Plan': { slug: 'digital-garden-plan', fallbackPrice: 19 },
    'The Easy Grow Box': { slug: 'starter-supply-kit', fallbackPrice: 49 },
    'The Guided Garden Box': { slug: 'complete-garden-kit', fallbackPrice: 99 }
  } as const;

  // Product features for each plan
  const PRODUCT_FEATURES = {
    'Digital Garden Plan': [
      'Your personalized plan, sent to your email!',
      'Digitized, zone‑matched planting plan',
      'Onboarding Video & Planting Tutorials',
      'Tailored care reminders'
    ],
    'The Easy Grow Box': [
      'A box delivered to your door!',
      '10 gallon grow bag',
      '4‑pack of Seeds; best for your growing zone',
      'Organic Fertilizer',
      'Self-watering spike'
    ],
    'The Guided Garden Box': [
      'A box delivered to your door!',
      '10 gallon grow bag',
      'Organic Soil (shipped separately)',
      '4‑pack of Seeds; best for your growing zone',
      'Organic Fertilizer',
      'Self-watering spike'
    ],
    // Common features for all physical boxes
    'Common Box Features': [
      'Digitized, zone‑matched planting plan',
      'Onboarding Videos & Tutorials',
      'Tailored care reminders'
    ],
  } as const;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedProduct, setSuggestedProduct] = useState<SuggestedProduct | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [allProducts, setAllProducts] = useState<ProductDetails[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Refs for auto-scrolling to each step
  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);
  const step4Ref = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Update URL whenever the step changes
  useEffect(() => {
    setSearchParams({ step: step.toString() }, { replace: true });
  }, [step, setSearchParams]);

  // Auto-scroll to current step when step changes
  useEffect(() => {
    const scrollToStep = () => {
      let targetRef: React.RefObject<HTMLDivElement> | null = null;

      switch (step) {
        case 1:
          targetRef = step1Ref;
          break;
        case 2:
          targetRef = step2Ref;
          break;
        case 3:
          targetRef = step3Ref;
          break;
        case 4:
          targetRef = step4Ref;
          break;
      }

      if (targetRef?.current) {
        setTimeout(() => {
          targetRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    };

    scrollToStep();
  }, [step]);

  // Auto-scroll to results when they're shown
  useEffect(() => {
    if (showResults && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [showResults]);

  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user, formData.email]);

  interface ProductDetails {
    id: string;
    name: string;
    price: number;
    slug: string;
    external_url: string | null;
    description: string;
  }

  // Fetch product details from Supabase
  useEffect(() => {
    async function fetchProductDetails() {
      setLoadingProducts(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, price, slug, external_url, description')
          .in('slug', Object.values(PRODUCT_INFO).map(info => info.slug));

        if (error) throw error;

        const products: ProductDetails[] = [];
        Object.entries(PRODUCT_INFO).forEach(([productName, info]) => {
          const product = data?.find(p => p.slug === info.slug);
          if (product) {
            products.push(product);
          } else {
            // Fallback if product not found in database
            products.push({
              id: `fallback-${info.slug}`,
              name: productName,
              price: info.fallbackPrice,
              slug: info.slug,
              external_url: null,
              description: `${productName} - Coming Soon`
            });
          }
        });
        setAllProducts(products);
      } catch (err) {
        console.error('Error fetching product details:', err);
        // Use fallback prices if fetch fails
        const fallbackProducts: ProductDetails[] = [];
        Object.entries(PRODUCT_INFO).forEach(([productName, info]) => {
          fallbackProducts.push({
            id: `fallback-${info.slug}`,
            name: productName,
            price: info.fallbackPrice,
            slug: info.slug,
            external_url: null,
            description: `${productName} - Coming Soon`
          });
        });
        setAllProducts(fallbackProducts);
      } finally {
        setLoadingProducts(false);
      }
    }

    fetchProductDetails();
  }, []);

  const handleNext = () => {
    setError(null);
    // Basic validation for current step before proceeding
    switch (step) {
      case 1: // Fall Garden Vibe
        if (!formData.fallGardenVibe || !formData.firstName.trim() || !formData.zipCode || formData.zipCode.length !== 5) {
          if (!formData.firstName.trim()) {
            setError('Please enter your first name.');
          } else if (!formData.zipCode || formData.zipCode.length !== 5) {
            setError('Please enter a valid 5-digit zip code.');
          } else {
            setError('Please select your fall garden vibe.');
          }
          return;
        }
        break;
      case 2: // Sun Exposure
        if (!formData.sunExposure) {
          setError('Please select your sun exposure.');
          return;
        }
        break;
      case 3: // Garden Setup
        if (!formData.gardenSetup) {
          setError('Please select your garden setup.');
          return;
        }
        break;
      case 4: // Email
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
          setError('Please enter a valid email address.');
          return;
        }
        break;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep(prev => prev - 1);
  };

  const determineSuggestedProduct = (data: OnboardingFormData): SuggestedProduct => {
    // Handle "Surprise Me" option - randomly select between the two themes
    if (data.fallGardenVibe === 'surprise_me') {
      const themes: FallGardenVibe[] = ['fall_greens', 'cool_weather_veggie'];
      const randomTheme = themes[Math.floor(Math.random() * themes.length)];
      // Update the form data with the randomly selected theme
      setFormData(prev => ({ ...prev, fallGardenVibe: randomTheme }));
    }
    
    if (data.gardenSetup === 'no_setup') {
      return 'The Guided Garden Box';
    }
    
    return 'Digital Garden Plan';
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      // Prepare data for the new submissions table
      const submissionData = {
        email: formData.email.toLowerCase(),
        first_name: formData.firstName,
        zip_code: formData.zipCode,
        box_theme: formData.fallGardenVibe,
        sun_exposure: formData.sunExposure,
        garden_setup: formData.gardenSetup,
        phone_number: formData.phone || null,
        source_page: 'grow'
      };

      // Use upsert to handle existing emails
      const { error: submissionError } = await supabase
        .from('grow_box_submissions')
        .insert(submissionData);

      if (submissionError) throw submissionError;

      let currentUserId = user?.id;

      if (!currentUserId) {
        // If user is not logged in, try to sign them in or create an unauth user
        const { error: signInError } = await retryOperation(() =>
          supabase.auth.signInWithOtp({
            email: formData.email.toLowerCase(),
            options: {
              emailRedirectTo: `${window.location.origin}/magic-link-callback`,
            }
          })
        );

        if (signInError) throw signInError;

        // For unauthenticated users, we might need to create a placeholder profile
        // or rely on the magic link callback to handle profile creation.
        // For this flow, we'll assume the magic link will eventually lead to profile creation.
        // We'll proceed to save the data if a user is available.
        // If not, the user will be prompted to check their email.
        setLoading(false);
        setShowResults(true); // Show results even if not fully logged in yet
        setSuggestedProduct(determineSuggestedProduct(formData));
        return;
      }

      setSuggestedProduct(determineSuggestedProduct(formData));
      setShowResults(true);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Step Context */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Let's Grow!</h2>
              <p className="text-gray-600">First, we need some basic info to create your personalized fall garden plan.</p>
            </div>

            {/* First Name and Zip Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your first name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                <input
                  type="text"
                  maxLength={5}
                  pattern="[0-9]*"
                  value={formData.zipCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., 90210"
                  required
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-full">
                <Sprout className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">What's your fall garden vibe?</h3>
                <p className="text-sm text-gray-600">Let's get growing — pick the box that fits you best!</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { 
                  value: 'fall_greens', 
                  emoji: '🥬', 
                  label: 'Fall Greens Garden', 
                  description: 'Crisp, fast-growing greens (like arugula + spinach) for the easiest fall salad garden you\'ve ever planted.' 
                },
                { 
                  value: 'cool_weather_veggie', 
                  emoji: '🥕', 
                  label: 'Cool Weather Veggie Box', 
                  description: 'Hearty roots and snappy greens (hello, radishes + peas) built for cozy meals and cool temps.' 
                },
                { 
                  value: 'surprise_me', 
                  emoji: '🎲', 
                  label: 'Surprise Me!', 
                  description: 'Let us pick the perfect fall garden theme for you based on your growing conditions.' 
                }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="fallGardenVibe"
                    value={option.value}
                    checked={formData.fallGardenVibe === option.value}
                    onChange={() => setFormData(prev => ({ ...prev, fallGardenVibe: option.value as FallGardenVibe }))}
                    className="text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-2xl">{option.emoji}</span>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            {/* Step Context */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Understanding Your Growing Conditions</h2>
              <p className="text-gray-600">This helps us recommend the right plants that will thrive in your specific light conditions.</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-full">
                <Sun className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {formData.firstName ? `${formData.firstName}, how much sun does your garden spot get each day?` : 'How much sun does your garden spot get each day?'}
                </h3>
                <p className="text-sm text-gray-600">We'll send you portable growing containers, so you can put them in the sunniest spot possible</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { 
                  value: 'full_sun',
                  emoji: '☀️',
                  label: 'Full Sun', 
                  description: '6+ hrs' 
                },
                {
                  value: 'part_sun',
                  emoji: '🌤',
                  label: 'Part Sun', 
                  description: '3–6 hrs; Morning light, afternoon shade? This counts!' 
                },
                {
                  value: 'shade',
                  emoji: '🌑',
                  label: 'Shade', 
                  description: '<3 hrs' 
                }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="sunExposure"
                    value={option.value}
                    checked={formData.sunExposure === option.value}
                    onChange={() => setFormData(prev => ({ ...prev, sunExposure: option.value as SunExposure }))}
                    className="text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-xl">{option.emoji}</span>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            {/* Step Context */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Your Garden Setup</h2>
              <p className="text-gray-600">This determines what supplies and containers we'll recommend for your garden.</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-full">
                <Home className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {formData.firstName ? `${formData.firstName}, what kind of space will you be planting in?` : 'What kind of space will you be planting in?'}
                </h3>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { value: 'in_ground_raised', label: 'In-ground or raised bed' },
                { value: 'containers_bags', label: 'Patio containers or Grow bags' },
                { value: 'no_setup', label: "Nothing yet - I'm just getting started!" }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="gardenSetup"
                    value={option.value}
                    checked={formData.gardenSetup === option.value}
                    onChange={() => setFormData(prev => ({ ...prev, gardenSetup: option.value as GardenSetup }))}
                    className="text-primary-500 focus:ring-primary-500"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            {/* Step Context */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Final Step - Contact Information</h2>
              <p className="text-gray-600">We'll use this to send your personalized garden plan and growing reminders.</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-full">
                <Mail className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {formData.firstName ? `Almost done, ${formData.firstName}! Your Contact Info` : 'Almost done! Your Contact Info'}
                </h3>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (required)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="your.email@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional, for reminders)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., 555-123-4567"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Garden Elements */}
      <div className="absolute top-10 left-10 opacity-10 hidden lg:block">
        <img 
          src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=150&h=150&fit=crop&crop=center" 
          alt="Garden seedlings"
          className="w-20 h-20 rounded-full object-cover"
        />
      </div>
      <div className="absolute top-1/4 right-16 opacity-15 hidden xl:block">
        <img 
          src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=200&h=200&fit=crop&crop=center" 
          alt="Fresh vegetables"
          className="w-24 h-24 rounded-full object-cover"
        />
      </div>
      <div className="absolute bottom-20 left-16 opacity-12 hidden lg:block">
        <img 
          src="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=180&h=180&fit=crop&crop=center" 
          alt="Garden tools"
          className="w-16 h-16 rounded-full object-cover"
        />
      </div>
      <div className="absolute top-1/3 left-1/4 opacity-8 hidden xl:block">
        <img 
          src="https://images.unsplash.com/photo-1592419044706-39796d40f98c?w=120&h=120&fit=crop&crop=center" 
          alt="Lettuce leaves"
          className="w-14 h-14 rounded-full object-cover"
        />
      </div>
      <div className="absolute bottom-1/3 right-20 opacity-10 hidden lg:block">
        <img 
          src="https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=160&h=160&fit=crop&crop=center" 
          alt="Radishes"
          className="w-18 h-18 rounded-full object-cover"
        />
      </div>
      
      <div className="max-w-3xl w-full bg-white rounded-lg shadow-xl p-8 relative z-10">
        <div className="flex justify-center mb-6">
          <Logo size="lg" showText={false} variant="full" />
        </div>
                
        <h1 className="text-2xl font-bold text-center mb-6">Your 5-Minute-a-Week Garden Is Here</h1>
         <p className="text-gray-600">Hand-picked seeds, simple steps, and zero guesswork — because you’ve got enough on your plate.</p>

        {showResults ? (
          <div ref={resultsRef} className="text-center space-y-6">
            <div>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-4">
                {formData.firstName ? `Perfect, ${formData.firstName}! Here's Your Recommendation` : 'Perfect! Here\'s Your Recommendation'}
              </h2>
              <p className="text-gray-700 mb-6">
                Based on your answers, we recommend:
              </p>
              
              {/* Display collected information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold mb-3">Your Garden Profile:</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Name:</strong> {formData.firstName}</p>
                  <p><strong>Selected theme:</strong> {
                    formData.fallGardenVibe === 'fall_greens' ? '🥬 Fall Greens Garden' :
                    formData.fallGardenVibe === 'cool_weather_veggie' ? '🥕 Cool Weather Veggie Box' :
                    formData.fallGardenVibe === 'surprise_me' ? '🎲 Surprise Me!' :
                    'Not selected'
                  }</p>
                  <p><strong>Zip code:</strong> {formData.zipCode}</p>
                  <p><strong>Sun exposure level:</strong> {
                    formData.sunExposure === 'full_sun' ? '☀️ Full Sun (6+ hrs)' :
                    formData.sunExposure === 'part_sun' ? '🌤 Part Sun (3–6 hrs)' :
                    formData.sunExposure === 'shade' ? '🌑 Shade (<3 hrs)' : 'Not selected'
                  }</p>
                  <p><strong>Garden setup type:</strong> {
                    formData.gardenSetup === 'in_ground_raised' ? 'In-ground or raised bed' :
                    formData.gardenSetup === 'containers_bags' ? 'Patio containers or grow bags' :
                    formData.gardenSetup === 'no_setup' ? "Don't have a setup yet" : 'Not selected'
                  }</p>
                </div>
              </div>
            </div>
            
            {suggestedProduct && allProducts.length > 0 && (
              <section className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
                {(() => {
                  const recommended = allProducts.find(p => p.name === suggestedProduct);
                  const backups = allProducts.filter(p => p.name !== suggestedProduct);
                  
                  return (
                    <>
                      {/* Recommended card first */}
                      {recommended && (
                        (() => {
                          const productFeatures = [...(PRODUCT_FEATURES[recommended.name as keyof typeof PRODUCT_FEATURES] || [])];
                          if (recommended.name !== 'Digital Garden Plan') {
                            productFeatures.push(...PRODUCT_FEATURES['Common Box Features']);
                          }
                          return (
                            <PackageCard
                              product={recommended}
                              highlighted
                              ctaLabel="Let's Grow"
                              features={productFeatures}
                            />
                          );
                        })()
                      )}

                      {/* Backup options */}
                      {backups.map(p => {
                        const productFeatures = [...(PRODUCT_FEATURES[p.name as keyof typeof PRODUCT_FEATURES] || [])];
                        if (p.name !== 'Digital Garden Plan') {
                          productFeatures.push(...PRODUCT_FEATURES['Common Box Features']);
                        }
                        return (
                          <PackageCard 
                            key={p.id} 
                            product={p}
                            ctaLabel="Let's Grow"
                            features={productFeatures}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </section>
            )}
            
            <div className="mt-8 text-center">
              <button
                onClick={() => navigate('/my-garden')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Continue to My Garden
              </button>
            </div>
            
            <p className="text-gray-500 text-sm">
              We've saved your preferences for a personalized experience!
            </p>
          </div>
        ) : (
          <>
            <div className="relative pt-1 mb-8">
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-primary-100">
                <div style={{ width: `${(step / 4) * 100}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary-500"></div>
              </div>
              <div className="text-center text-sm text-gray-600">Step {step} of 4</div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                <X className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            <div
              ref={step === 1 ? step1Ref : step === 2 ? step2Ref : step === 3 ? step3Ref : step === 4 ? step4Ref : null}
            >
              {renderStep()}
            </div>

            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  onClick={handleBack}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
              )}
              {step < 4 ? (
                <button
                  onClick={handleNext}
                  className={`px-6 py-3 rounded-lg text-white transition-colors ${
                    loading ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'
                  } ${step === 1 ? 'ml-auto' : ''}`}
                  disabled={loading}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className={`px-6 py-3 rounded-lg text-white transition-colors flex items-center justify-center gap-2 ${
                    loading ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'
                  } ml-auto`}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      <span>{formData.firstName ? `Show ${formData.firstName}'s Garden Plan` : 'Show My Garden Plan'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}